'use client'

import Image from "next/image";
import Link from "next/link";
import {useRouter} from "next/navigation";
import {useCallback, useEffect, useRef, useState} from "react";
import type {ChangeEvent, FormEvent, KeyboardEvent} from "react";
import {
  AlertCircle,
  ArrowLeft,
  Bug,
  CheckCircle2,
  ChevronRight,
  CirclePlus,
  Clock,
  FileText,
  Gift,
  HelpCircle,
  Inbox,
  KeyRound,
  LifeBuoy,
  MessageSquare,
  Paperclip,
  RefreshCw,
  Rocket,
  Search,
  Send,
  Shield,
  X,
} from "lucide-react";

import Logo from "@/assets/logo.png";
import {Button} from "@/components/ui/button";
import {ApiClientError} from "@/lib/api-client";
import {
  addSupportTicketMessage,
  createSupportTicket,
  getSupportTicket,
  listSupportTickets,
  supportAttachmentUrl,
  updateSupportTicket,
  uploadSupportTicketAttachment,
} from "@/lib/support";
import type {
  SupportTicket,
  SupportTicketCategory,
  SupportTicketPriority,
  SupportTicketStatus,
} from "@/lib/support";

const STATUS_LABELS: Record<SupportTicketStatus, string> = {
  open: "Eingegangen",
  closed: "Erledigt",
};

const STATUS_STYLES: Record<SupportTicketStatus, string> = {
  open: "bg-sky-400/10 text-sky-300",
  in_progress: "bg-amber-400/10 text-amber-300",
  waiting_user: "bg-violet-400/10 text-violet-300",
  closed: "bg-slate-500/10 text-slate-400",
};

const PRIORITY_LABELS: Record<SupportTicketPriority, string> = {
  low: "Niedrig",
  normal: "Normal",
  high: "Hoch",
  urgent: "Dringend",
};

const PRIORITY_DOTS: Record<SupportTicketPriority, string> = {
  low: "bg-sky-400",
  normal: "bg-slate-500",
  high: "bg-amber-400",
  urgent: "bg-red-500",
};

const CATEGORY_LABELS: Record<SupportTicketCategory, string> = {
  general: "Frage",
  technical: "Technisches Problem",
  account: "Mein Konto",
  billing: "Abrechnung",
  bug: "Fehler",
  feature: "Idee / Wunsch",
};

const STATUS_HINTS: Record<SupportTicketStatus, string> = {
  open: "Deine Anfrage ist bei uns eingegangen. Wir antworten dir hier im Verlauf.",
  in_progress: "Unser Team kümmert sich gerade darum.",
  waiting_user: "Wir brauchen noch eine Antwort von dir. Schreib sie unten in das Feld.",
  closed: "Diese Anfrage ist erledigt. Besteht das Problem noch, kannst du sie wieder öffnen.",
};

const CATEGORY_OPTIONS: {value: SupportTicketCategory; title: string; hint: string}[] = [
  {value: "general", title: "Frage", hint: "Ich möchte etwas wissen"},
  {value: "bug", title: "Fehler", hint: "Etwas funktioniert nicht"},
  {value: "feature", title: "Idee", hint: "Ich wünsche mir etwas"},
  {value: "account", title: "Mein Konto", hint: "Login, Passwort, Profil"},
];

const FAQ = [
  {
    icon: Rocket,
    question: "Wie lege ich ein neues Projekt an?",
    answer: "Erstelle im Dashboard einen Workspace, lade deine Teamkollegen ein und starte direkt mit dem Kanban-Board.",
  },
  {
    icon: KeyRound,
    question: "Passwort vergessen?",
    answer: "Über die Login-Maske kannst du dein Passwort jederzeit mit der „Passwort vergessen“-Funktion erneuern.",
  },
  {
    icon: Gift,
    question: "Kostet DevPlaner etwas?",
    answer: "Nein. DevPlaner ist kostenlos – ohne Abo und ohne Zahlungsdaten.",
  },
  {
    icon: Bug,
    question: "Wie melde ich einen Bug?",
    answer: "Schreib uns über „Neue Anfrage“ und hänge am besten einen Screenshot an. Das hilft uns bei der Analyse.",
  },
];

// Wiederverwendete Tailwind-Klassen
const panel = "flex min-h-0 flex-col overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0a101c]/80";
const field = "w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-blue-400/50 focus:ring-2 focus:ring-blue-400/20";
const select = "h-9 rounded-lg border border-white/10 bg-[#0b111e] px-2.5 text-xs text-slate-200 outline-none transition-colors focus:border-blue-400/50 focus:ring-2 focus:ring-blue-400/20 disabled:opacity-50";
const iconBtn = "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/[0.03] text-slate-400 transition-colors hover:bg-white/[0.07] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 disabled:opacity-50";
const primaryBtn = "rounded-lg bg-blue-500 text-white hover:bg-blue-400";

interface CreateFormState {
  title: string;
  description: string;
  category: SupportTicketCategory;
  priority: SupportTicketPriority;
}

const EMPTY_FORM: CreateFormState = {
  title: "",
  description: "",
  category: "general",
  priority: "normal",
};

const MAX_FILE_SIZE = 10 * 1024 * 1024;

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "–";
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function errorText(error: unknown): string {
  if (error instanceof ApiClientError) return error.message;
  return error instanceof Error ? error.message : "Ein unerwarteter Fehler ist aufgetreten.";
}

export default function SupportPage() {
  const router = useRouter();
  const attachmentInput = useRef<HTMLInputElement>(null);
  const feedRef = useRef<HTMLDivElement>(null);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [mobileDetail, setMobileDetail] = useState(false);
  const [canManage, setCanManage] = useState(false);
  const [scope, setScope] = useState<"mine" | "all">("mine");
  const [statusFilter, setStatusFilter] = useState<SupportTicketStatus | "">("");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<"tickets" | "faq">("tickets");
  const [loadingList, setLoadingList] = useState(true);
  const [loadingTicket, setLoadingTicket] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateFormState>(EMPTY_FORM);
  const [createFile, setCreateFile] = useState<File | null>(null);
  const [creating, setCreating] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [updating, setUpdating] = useState(false);

  const handleApiError = useCallback((reason: unknown) => {
    if (reason instanceof ApiClientError && reason.statusCode === 401) {
      router.replace("/login?next=/support");
      return;
    }
    setError(errorText(reason));
  }, [router]);

  const loadTickets = useCallback(async (showLoader = false) => {
    if (showLoader) setLoadingList(true);
    try {
      const response = await listSupportTickets(scope, statusFilter);
      setTickets(response.tickets);
      setCanManage(response.canManage);
      setSelectedUid((current) => {
        if (current && response.tickets.some((t) => t.uid === current)) return current;
        return response.tickets[0]?.uid ?? null;
      });
    } catch (reason) {
      handleApiError(reason);
    } finally {
      setLoadingList(false);
      setRefreshing(false);
    }
  }, [handleApiError, scope, statusFilter]);

  const loadSelectedTicket = useCallback(async (ticketUid: string, showLoader = false) => {
    if (showLoader) setLoadingTicket(true);
    try {
      const response = await getSupportTicket(ticketUid);
      setSelectedTicket(response.ticket);
      setCanManage(response.canManage);
    } catch (reason) {
      handleApiError(reason);
    } finally {
      setLoadingTicket(false);
    }
  }, [handleApiError]);

  useEffect(() => {
    void loadTickets(true);
    const interval = window.setInterval(() => void loadTickets(false), 15_000);
    return () => window.clearInterval(interval);
  }, [loadTickets]);

  useEffect(() => {
    if (!selectedUid) {
      setSelectedTicket(null);
      return;
    }
    void loadSelectedTicket(selectedUid, true);
    const interval = window.setInterval(() => void loadSelectedTicket(selectedUid, false), 6_000);
    return () => window.clearInterval(interval);
  }, [loadSelectedTicket, selectedUid]);

  // Verlauf springt nach unten, wenn ein Ticket geöffnet wird oder eine neue Nachricht eintrifft
  const messageCount = selectedTicket?.messages.length ?? 0;
  useEffect(() => {
    const feed = feedRef.current;
    if (feed) feed.scrollTop = feed.scrollHeight;
  }, [selectedTicket?.uid, messageCount]);

  // Modal mit Escape schließen
  useEffect(() => {
    if (!createOpen) return;
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape" && !creating) setCreateOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [createOpen, creating]);

  const refreshAll = async () => {
    setRefreshing(true);
    await loadTickets(false);
    if (selectedUid) await loadSelectedTicket(selectedUid, false);
  };

  const submitTicket = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (creating) return;
    setCreating(true);
    setError(null);
    if (createFile && createFile.size > MAX_FILE_SIZE) {
      setError("Die Datei darf maximal 10 MB groß sein.");
      setCreating(false);
      return;
    }
    try {
      const response = await createSupportTicket({
        ...createForm,
        title: createForm.title.trim(),
        description: createForm.description.trim(),
      });
      if (createFile) {
        await uploadSupportTicketAttachment(response.ticket.uid, createFile);
      }
      setCreateForm(EMPTY_FORM);
      setCreateFile(null);
      setCreateOpen(false);
      setSelectedUid(response.ticket.uid);
      setMobileDetail(true);
      setActiveTab("tickets");
      setNotice("Danke! Deine Anfrage ist bei uns eingegangen. Wir antworten dir hier im Verlauf.");
      await loadTickets(false);
      await loadSelectedTicket(response.ticket.uid, false);
    } catch (reason) {
      handleApiError(reason);
    } finally {
      setCreating(false);
    }
  };

  const sendReply = async (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    const content = reply.trim();
    if (!selectedUid || !content || sending) return;
    setSending(true);
    setError(null);
    try {
      await addSupportTicketMessage(selectedUid, content);
      setReply("");
      await Promise.all([
        loadSelectedTicket(selectedUid, false),
        loadTickets(false),
      ]);
    } catch (reason) {
      handleApiError(reason);
    } finally {
      setSending(false);
    }
  };

  const onReplyKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void sendReply();
    }
  };

  const uploadAttachment = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !selectedUid || uploading) return;
    if (file.size > MAX_FILE_SIZE) {
      setError("Die Datei darf maximal 10 MB groß sein.");
      return;
    }
    setUploading(true);
    setError(null);
    try {
      await uploadSupportTicketAttachment(selectedUid, file);
      await loadSelectedTicket(selectedUid, false);
    } catch (reason) {
      handleApiError(reason);
    } finally {
      setUploading(false);
    }
  };

  const changeTicket = async (input: {status?: SupportTicketStatus; priority?: SupportTicketPriority}) => {
    if (!selectedUid || updating) return;
    setUpdating(true);
    setError(null);
    try {
      const response = await updateSupportTicket(selectedUid, input);
      setSelectedTicket(response.ticket);
      await loadTickets(false);
    } catch (reason) {
      handleApiError(reason);
    } finally {
      setUpdating(false);
    }
  };

  const filteredTickets = tickets.filter((ticket) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      ticket.title.toLowerCase().includes(q) ||
      ticket.description.toLowerCase().includes(q) ||
      ticket.uid.toLowerCase().includes(q)
    );
  });

  const openCount = tickets.filter((t) => t.status !== "closed").length;

  // Beschreibung und Antworten als ein gemeinsamer Verlauf
  const thread = selectedTicket
    ? [
        {
          uid: "description",
          staff: false,
          name: selectedTicket.creatorDisplayName || "Du",
          at: selectedTicket.createdAt,
          text: selectedTicket.description,
        },
        ...selectedTicket.messages.map((m) => ({
          uid: m.uid,
          staff: m.senderType === "staff",
          name: m.senderType === "staff" ? "Support-Team" : m.senderDisplayName,
          at: m.createdAt,
          text: m.content,
        })),
      ]
    : [];

  return (
    <div className="relative min-h-screen overflow-x-clip bg-[#070b14] text-slate-100 selection:bg-blue-500/30">
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute -left-52 -top-52 h-[34rem] w-[34rem] rounded-full bg-blue-600/15 blur-[130px]" />
        <div className="absolute right-[-16rem] top-[24rem] h-[30rem] w-[30rem] rounded-full bg-cyan-500/10 blur-[140px]" />
      </div>

      <div className="relative mx-auto w-full max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
        <header className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/[0.08] bg-[#0a0f1d]/85 px-4 py-3 shadow-2xl shadow-black/20 backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <Link href="/administration" className={iconBtn} aria-label="Zurück">
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <Image src={Logo} alt="" className="h-9 w-9 rounded-xl object-cover ring-1 ring-white/10" />
            <div className="leading-tight">
              <h1 className="font-semibold tracking-tight text-white">Hilfe & Support</h1>
              <p className="hidden text-xs text-slate-500 sm:block">Wir helfen dir gern weiter</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex rounded-xl border border-white/[0.08] bg-white/[0.02] p-0.5">
              {([["tickets", "Meine Anfragen", Inbox], ["faq", "Häufige Fragen", HelpCircle]] as const).map(([id, label, Icon]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveTab(id)}
                  aria-pressed={activeTab === id}
                  className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
                    activeTab === id ? "bg-blue-500/15 text-blue-300" : "text-slate-400 hover:text-white"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                  {id === "tickets" && openCount > 0 && (
                    <span className="rounded-full bg-blue-500 px-1.5 text-[10px] font-semibold text-white">{openCount}</span>
                  )}
                </button>
              ))}
            </div>
            <Button size="sm" className={primaryBtn} onClick={() => setCreateOpen(true)}>
              <CirclePlus className="mr-1.5 h-4 w-4" />
              Neue Anfrage
            </Button>
          </div>
        </header>

        {error && (
          <div role="alert" className="mt-4 flex items-center gap-3 rounded-xl border border-red-400/25 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1">{error}</span>
            <button type="button" onClick={() => setError(null)} aria-label="Fehlermeldung schließen" className="rounded-md p-1 hover:bg-white/10">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {notice && (
          <div role="status" className="mt-4 flex items-center gap-3 rounded-xl border border-emerald-400/25 bg-emerald-500/10 px-4 py-2.5 text-sm text-emerald-300">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1">{notice}</span>
            <button type="button" onClick={() => setNotice(null)} aria-label="Hinweis schließen" className="rounded-md p-1 hover:bg-white/10">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {activeTab === "faq" ? (
          <section className="mx-auto mt-6 max-w-3xl" aria-label="Häufige Fragen">
            <h2 className="text-2xl font-semibold tracking-tight text-white">Häufige Fragen</h2>
            <p className="mt-2 text-sm text-slate-400">Vielleicht ist deine Frage hier schon beantwortet.</p>
            <div className="mt-6 divide-y divide-white/[0.08] border-y border-white/[0.08]">
              {FAQ.map(({icon: Icon, question, answer}) => (
                <details key={question} className="group py-1">
                  <summary className="flex cursor-pointer list-none items-center gap-3 py-4 text-left text-sm font-medium text-white [&::-webkit-details-marker]:hidden">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-500/10 text-blue-400 ring-1 ring-blue-400/10">
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="flex-1">{question}</span>
                    <span className="flex h-6 w-6 items-center justify-center rounded-full border border-white/10 text-base font-light text-blue-300 transition-transform group-open:rotate-45">+</span>
                  </summary>
                  <p className="pb-4 pl-11 pr-8 text-sm leading-6 text-slate-400">{answer}</p>
                </details>
              ))}
            </div>
            <div className="mt-6 flex items-center justify-between gap-4 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-4">
              <p className="text-sm text-slate-400">Nichts dabei? Wir helfen dir persönlich weiter.</p>
              <Button size="sm" className={primaryBtn} onClick={() => setCreateOpen(true)}>Nachricht an den Support</Button>
            </div>
          </section>
        ) : !canManage && !loadingList && tickets.length === 0 && !statusFilter ? (
          <section className="mx-auto mt-10 max-w-2xl text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-500/10 text-blue-400 ring-1 ring-blue-400/10">
              <LifeBuoy className="h-7 w-7" />
            </span>
            <h2 className="mt-5 text-balance text-3xl font-semibold tracking-tight text-white">Wie können wir dir helfen?</h2>
            <p className="mx-auto mt-3 max-w-md leading-7 text-slate-400">
              Schau zuerst in die häufigen Fragen – oft ist die Antwort schon dabei. Sonst schreib uns direkt, wir antworten dir hier auf der Seite.
            </p>
            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <Button size="lg" className={`${primaryBtn} h-12 px-6`} onClick={() => setCreateOpen(true)}>
                <CirclePlus className="mr-2 h-4 w-4" />
                Nachricht an den Support
              </Button>
              <Button size="lg" variant="outline" className="h-12 rounded-lg border-white/10 bg-white/[0.02] px-6 text-slate-200 hover:bg-white/[0.06] hover:text-white" onClick={() => setActiveTab("faq")}>
                Häufige Fragen ansehen
              </Button>
            </div>
          </section>
        ) : (
          <div className="sp-shell">
            {/* Ticket-Liste */}
            <aside className={`${panel} ${mobileDetail ? "hidden lg:flex" : "flex"}`}>
              <div className="space-y-2.5 border-b border-white/[0.07] p-3">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500" />
                  <input
                    type="text"
                    placeholder="Anfragen durchsuchen …"
                    aria-label="Anfragen durchsuchen"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`${field} h-9 pl-9 pr-8`}
                  />
                  {searchQuery && (
                    <button type="button" onClick={() => setSearchQuery("")} aria-label="Suche leeren" className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-500 hover:text-white">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>

              <div className="sp-scroll flex-1 space-y-1.5 p-2">
                {loadingList ? (
                  <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-slate-500">
                    <RefreshCw className="h-5 w-5 animate-spin" />
                    Lade Anfragen …
                  </div>
                ) : filteredTickets.length === 0 ? (
                  <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center text-sm text-slate-500">
                    <Inbox className="h-6 w-6" />
                    <p>{searchQuery || statusFilter ? "Keine Anfragen für diese Auswahl." : "Du hast noch keine Anfragen."}</p>
                    {!searchQuery && !statusFilter && (
                      <Button size="sm" className={primaryBtn} onClick={() => setCreateOpen(true)}>Erste Anfrage schreiben</Button>
                    )}
                  </div>
                ) : (
                  filteredTickets.map((t) => {
                    const selected = t.uid === selectedUid;
                    return (
                      <button
                        key={t.uid}
                        type="button"
                        aria-current={selected}
                        onClick={() => {
                          setSelectedUid(t.uid);
                          setMobileDetail(true);
                        }}
                        className={`block w-full rounded-xl border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
                          selected ? "border-blue-400/40 bg-blue-500/10" : "border-transparent hover:border-white/10 hover:bg-white/[0.03]"
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <span className={`h-2 w-2 shrink-0 rounded-full ${PRIORITY_DOTS[t.priority]}`} title={`Priorität: ${PRIORITY_LABELS[t.priority]}`} />
                          <span className="min-w-0 flex-1 truncate text-sm font-medium text-white">{t.title}</span>
                          <span className="shrink-0 text-[11px] text-slate-500">{formatDate(t.updatedAt)}</span>
                        </span>
                        <span className="mt-1.5 flex items-center gap-2 pl-4 text-[11px] text-slate-500">
                          <span className={`rounded-md px-1.5 py-0.5 font-medium ${STATUS_STYLES[t.status]}`}>{STATUS_LABELS[t.status]}</span>
                          <span className="flex items-center gap-1"><MessageSquare className="h-3 w-3" /> {t.messageCount}</span>
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </aside>

            {/* Detailansicht */}
            <section className={`${panel} ${mobileDetail ? "flex" : "hidden lg:flex"}`} aria-label="Verlauf der Anfrage">
              {loadingTicket && !selectedTicket ? (
                <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-slate-500">
                  <RefreshCw className="h-6 w-6 animate-spin" />
                  Anfrage wird geladen …
                </div>
              ) : !selectedTicket ? (
                <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-500/10 text-blue-400 ring-1 ring-blue-400/10">
                    <LifeBuoy className="h-6 w-6" />
                  </span>
                  <h3 className="font-medium text-white">Wähle eine Anfrage aus</h3>
                  <p className="max-w-xs text-sm text-slate-500">Klicke links auf eine Anfrage, um den Verlauf zu sehen und zu antworten. Oder schreib uns oben rechts über „Neue Anfrage“.</p>
                </div>
              ) : (
                <>
             

                  <p className={`border-b border-white/[0.07] px-4 py-2.5 text-xs sm:px-5 ${selectedTicket.status === "waiting_user" ? "bg-violet-500/10 text-violet-200" : "bg-white/[0.02] text-slate-400"}`}>
                    {STATUS_HINTS[selectedTicket.status]}
                  </p>

                  <div ref={feedRef} className="sp-scroll flex-1 space-y-4 p-4 sm:p-5">
                    {canManage && (
                      <div className="flex items-center gap-3 rounded-xl border border-white/[0.07] bg-white/[0.02] px-4 py-3">
                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500 text-sm font-semibold text-white">
                          {selectedTicket.creatorDisplayName.slice(0, 1).toUpperCase()}
                        </span>
                        <div className="min-w-0 text-xs">
                          <p className="text-slate-500">Erstellt von</p>
                          <p className="truncate text-slate-300"><strong className="font-medium text-white">{selectedTicket.creatorDisplayName}</strong> · {selectedTicket.workspaceName}</p>
                        </div>
                        <Shield className="ml-auto h-4 w-4 shrink-0 text-sky-400" />
                      </div>
                    )}

                    {thread.map((m) => {
                      const own = canManage ? m.staff : !m.staff;
                      return (
                        <div key={m.uid} className={`flex items-end gap-2 ${own ? "flex-row-reverse" : ""}`}>
                          <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white ${m.staff ? "bg-blue-500" : "bg-slate-600"}`}>
                            {m.staff ? <LifeBuoy className="h-3.5 w-3.5" /> : (m.name || "?").slice(0, 1).toUpperCase()}
                          </span>
                          <div className={`flex max-w-[85%] flex-col sm:max-w-[75%] ${own ? "items-end" : "items-start"}`}>
                            <span className="mb-1 px-1 text-[11px] text-slate-500">{own ? "Du" : m.name} · {formatDate(m.at)}</span>
                            <p className={`whitespace-pre-wrap break-words rounded-2xl px-4 py-2.5 text-sm leading-6 ${
                              own ? "rounded-br-md bg-blue-500 text-white" : "rounded-bl-md border border-white/[0.08] bg-white/[0.04] text-slate-200"
                            }`}>{m.text}</p>
                          </div>
                        </div>
                      );
                    })}

                    {selectedTicket.attachments.length > 0 && (
                      <div>
                        <p className="mb-2 flex items-center gap-1.5 text-xs text-slate-500"><Paperclip className="h-3.5 w-3.5" /> Anhänge</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedTicket.attachments.map((att) => (
                            <a
                              key={att.uid}
                              href={supportAttachmentUrl(att.filePath)}
                              target="_blank"
                              rel="noreferrer"
                              className="flex items-center gap-2 rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-xs text-slate-300 transition-colors hover:border-white/20 hover:text-white"
                            >
                              <FileText className="h-3.5 w-3.5 text-blue-400" />
                              <span className="max-w-[12rem] truncate">{att.originalFilename}</span>
                              <ChevronRight className="h-3.5 w-3.5 text-slate-500" />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <form className="space-y-2 border-t border-white/[0.07] bg-black/20 p-3 sm:px-5" onSubmit={sendReply}>
                    {selectedTicket.status === "closed" && !canManage ? (
                      <p className="flex items-center gap-2 py-1 text-sm text-slate-400">
                        <CheckCircle2 className="h-4 w-4 text-emerald-400" /> Diese Anfrage ist erledigt.
                      </p>
                    ) : (
                      <>
                        <div className="flex items-end gap-2 rounded-2xl border border-white/10 bg-white/[0.03] p-1.5 transition-colors focus-within:border-blue-400/50 focus-within:ring-2 focus-within:ring-blue-400/20">
                          <input ref={attachmentInput} type="file" hidden accept="image/*,.pdf,.txt,.zip" onChange={uploadAttachment} />
                          <button
                            type="button"
                            onClick={() => attachmentInput.current?.click()}
                            disabled={uploading}
                            aria-label="Datei anhängen"
                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-slate-400 transition-colors hover:bg-white/[0.07] hover:text-white disabled:opacity-50"
                          >
                            {uploading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                          </button>
                          <textarea
                            rows={1}
                            placeholder="Nachricht schreiben …"
                            aria-label="Nachricht"
                            value={reply}
                            onChange={(e) => setReply(e.target.value)}
                            onKeyDown={onReplyKeyDown}
                            className="max-h-32 flex-1 resize-none bg-transparent px-1 py-2 text-sm text-white outline-none placeholder:text-slate-500"
                          />
                          <button
                            type="submit"
                            disabled={!reply.trim() || sending}
                            aria-label="Senden"
                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-500 text-white transition-colors hover:bg-blue-400 disabled:opacity-40"
                          >
                            {sending ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                          </button>
                        </div>
                        <p className="hidden px-1 text-[11px] text-slate-600 sm:block">Enter zum Senden · Shift + Enter für neue Zeile</p>
                      </>
                    )}
                  </form>
                </>
              )}
            </section>
          </div>
        )}
      </div>

      {createOpen && (
        <div
          className="fixed inset-0 z-[100] grid place-items-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget && !creating) setCreateOpen(false); }}
        >
          <div role="dialog" aria-modal="true" aria-labelledby="new-ticket-title" className="w-full max-w-lg overflow-hidden rounded-2xl border border-white/10 bg-[#0b111e] shadow-[0_30px_100px_rgba(0,0,0,0.6)]">
            <div className="flex items-start justify-between gap-4 border-b border-white/[0.07] px-5 py-4">
              <div>
                <h3 id="new-ticket-title" className="font-semibold text-white">Nachricht an den Support</h3>
                <p className="mt-0.5 text-xs text-slate-500">Wir antworten dir hier auf der Seite im Verlauf.</p>
              </div>
              <button type="button" onClick={() => setCreateOpen(false)} aria-label="Schließen" className="rounded-md p-1 text-slate-500 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
            <form onSubmit={submitTicket} className="space-y-4 p-5">
              <div role="radiogroup" aria-label="Worum geht es?">
                <p className="mb-2 text-xs font-medium text-slate-400">Worum geht es?</p>
                <div className="grid grid-cols-2 gap-2">
                  {CATEGORY_OPTIONS.map((option) => {
                    const checked = createForm.category === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={checked}
                        onClick={() => setCreateForm({...createForm, category: option.value})}
                        className={`rounded-xl border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
                          checked ? "border-blue-400/50 bg-blue-500/10" : "border-white/10 hover:bg-white/[0.04]"
                        }`}
                      >
                        <span className="block text-sm font-medium text-white">{option.title}</span>
                        <span className="mt-0.5 block text-xs text-slate-400">{option.hint}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <label className="block space-y-1.5 text-xs font-medium text-slate-400">
                Kurzer Titel
                <input
                  type="text"
                  required
                  placeholder="z. B. Ich kann keine Aufgabe erstellen"
                  value={createForm.title}
                  onChange={(e) => setCreateForm({...createForm, title: e.target.value})}
                  className={`${field} h-10`}
                />
              </label>

              <label className="block space-y-1.5 text-xs font-medium text-slate-400">
                Was ist passiert?
                <textarea
                  rows={5}
                  required
                  placeholder="Was wolltest du tun, und was ist stattdessen passiert? Je genauer, desto schneller können wir helfen."
                  value={createForm.description}
                  onChange={(e) => setCreateForm({...createForm, description: e.target.value})}
                  className={`${field} resize-none py-2.5`}
                />
              </label>

              <label className="relative flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-white/15 p-3 transition-colors hover:border-blue-400/40 hover:bg-blue-500/[0.04]">
                <Paperclip className="h-4 w-4 shrink-0 text-blue-400" />
                <span className="min-w-0 text-xs">
                  <strong className="block truncate font-medium text-white">{createFile ? createFile.name : "Screenshot oder Datei anhängen (optional)"}</strong>
                  <span className="text-slate-500">Maximal 10 MB · Bilder, PDF, TXT, ZIP</span>
                </span>
                <input type="file" accept="image/*,.pdf,.txt,.zip" onChange={(e) => setCreateFile(e.target.files?.[0] ?? null)} className="absolute inset-0 cursor-pointer opacity-0" />
              </label>

              <label className="flex cursor-pointer items-center gap-2.5 text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={createForm.priority === "high"}
                  onChange={(e) => setCreateForm({...createForm, priority: e.target.checked ? "high" : "normal"})}
                  className="h-4 w-4 rounded border-white/20 accent-blue-500"
                />
                Das ist dringend – ich komme gerade nicht weiter
              </label>

              <div className="flex justify-end gap-2 pt-1">
                <Button type="button" variant="outline" size="sm" className="rounded-lg border-white/10 bg-transparent text-slate-300 hover:bg-white/[0.06] hover:text-white" onClick={() => setCreateOpen(false)} disabled={creating}>
                  Abbrechen
                </Button>
                <Button type="submit" size="sm" className={primaryBtn} disabled={creating}>
                  {creating ? <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1.5 h-3.5 w-3.5" />}
                  Anfrage senden
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx global>{`
        .sp-shell {
          display: grid;
          gap: 1rem;
          margin-top: 1rem;
          grid-template-columns: minmax(0, 1fr);
          height: calc(100vh - 8.5rem);
          min-height: 32rem;
        }
        .sp-shell > * { min-height: 0; }
        .sp-scroll { overflow-y: auto; min-height: 0; }
        @media (min-width: 1024px) {
          .sp-shell { grid-template-columns: 18rem minmax(0, 1fr); }
        }
      `}</style>
    </div>
  );
}
